import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { RefreshCw, Shield, AlertCircle, CheckCircle2, Clock3, Sparkles } from 'lucide-react'
import { getFacilities, getFacilityFeatureFlags } from '../../lib/services/facilities'
import {
  batchVerifyInsurance,
  generateInsuranceAiAssist,
  getInsuranceDashboard,
  type InsuranceEligibilityInput,
  updateInsuranceAuthWorkflow,
  verifyInsuranceVisit,
  type AuthorizationStatus,
} from '../../lib/services/insuranceVerification'

const STATUS_STYLES: Record<string, string> = {
  verified: 'bg-emerald-50 text-emerald-700',
  manual_review_required: 'bg-amber-50 text-amber-700',
  provider_unconfigured: 'bg-slate-100 text-slate-700',
  failed: 'bg-red-50 text-red-700',
}

function formatDate(value: string): string {
  const parsed = new Date(value)
  return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleDateString()
}

function formatCurrencyFromCents(value: number | null): string {
  if (typeof value !== 'number') return 'Unavailable'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value / 100)
}

function readSummaryString(summary: Record<string, unknown> | undefined, key: string): string | null {
  const value = summary?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function readSummaryNumber(summary: Record<string, unknown> | undefined, key: string): number | null {
  const value = summary?.[key]
  return typeof value === 'number' ? value : null
}

export function InsuranceDashboardPage() {
  const [selectedFacility, setSelectedFacility] = useState('')
  const [payerNames, setPayerNames] = useState<Record<string, string>>({})
  const [eligibilityDrafts, setEligibilityDrafts] = useState<Record<string, InsuranceEligibilityInput>>({})
  const [authDrafts, setAuthDrafts] = useState<Record<string, { authStatus: AuthorizationStatus; note: string; followUpDate: string }>>({})
  const [viewMode, setViewMode] = useState<'all' | 'auth_required' | 'pending_auth'>('all')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const facilitiesQuery = useQuery({
    queryKey: ['insurance-facilities'],
    queryFn: getFacilities,
  })
  const featureFlagsQuery = useQuery({
    queryKey: ['insurance-feature-flags'],
    queryFn: () => getFacilityFeatureFlags(),
  })
  const dashboardQuery = useQuery({
    queryKey: ['insurance-dashboard', selectedFacility],
    queryFn: () => getInsuranceDashboard(selectedFacility || undefined),
  })

  const verifyMutation = useMutation({
    mutationFn: async (payload: { visitId: string; payerName?: string; eligibility?: InsuranceEligibilityInput }) =>
      await verifyInsuranceVisit(payload.visitId, payload.payerName, payload.eligibility),
    onSuccess: async () => {
      await dashboardQuery.refetch()
    },
  })

  const batchMutation = useMutation({
    mutationFn: async () => await batchVerifyInsurance(selectedFacility || undefined),
    onSuccess: async (response) => {
      setMessage(`Batch verification processed ${response.processed_count} visit${response.processed_count === 1 ? '' : 's'}.`)
      await dashboardQuery.refetch()
    },
  })

  const authWorkflowMutation = useMutation({
    mutationFn: updateInsuranceAuthWorkflow,
    onSuccess: async () => {
      await dashboardQuery.refetch()
    },
  })

  const aiAssistMutation = useMutation({
    mutationFn: generateInsuranceAiAssist,
    onSuccess: async () => {
      await dashboardQuery.refetch()
    },
  })

  const insuranceEnabled = useMemo(
    () => (featureFlagsQuery.data ?? []).some((flag) => flag.feature_key === 'insurance_verification' && flag.enabled),
    [featureFlagsQuery.data],
  )

  const visits = dashboardQuery.data?.visits ?? []
  const verifiedCount = visits.filter((visit) => visit.verification?.verification_status === 'verified').length
  const pendingCount = visits.filter((visit) => !visit.verification || visit.verification.verification_status !== 'verified').length
  const authRequiredCount = visits.filter((visit) => Boolean(visit.verification?.auth_required)).length
  const pendingAuthCount = visits.filter((visit) => visit.verification?.auth_status === 'pending').length
  const externalProviderMode = dashboardQuery.data?.provider_mode === 'external'
  const filteredVisits = visits.filter((visit) => {
    if (viewMode === 'auth_required') return Boolean(visit.verification?.auth_required)
    if (viewMode === 'pending_auth') return visit.verification?.auth_status === 'pending'
    return true
  })

  async function handleVerify(visitId: string) {
    setError(null)
    setMessage(null)
    try {
      const payerName = payerNames[visitId]?.trim()
      const eligibility = eligibilityDrafts[visitId]
      const verification = await verifyMutation.mutateAsync({
        visitId,
        payerName: payerName || undefined,
        eligibility,
      })
      setMessage(`Verification updated for visit ${visitId.slice(0, 8)} via ${verification.provider_mode} mode.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify insurance')
    }
  }

  async function handleBatchVerify() {
    setError(null)
    setMessage(null)
    try {
      await batchMutation.mutateAsync()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to batch verify insurance')
    }
  }

  async function handleAuthWorkflowSave(visitId: string) {
    const draft = authDrafts[visitId]
    if (!draft) return
    setError(null)
    setMessage(null)
    try {
      const authRequired = draft.authStatus === 'not_required' ? false : ['required', 'pending', 'approved', 'denied'].includes(draft.authStatus)
      const verification = await authWorkflowMutation.mutateAsync({
        visitId,
        authStatus: draft.authStatus,
        authRequired,
        authWorkflowNote: draft.note || undefined,
        authFollowUpDate: draft.followUpDate || undefined,
      })
      setMessage(`Authorization workflow updated for visit ${visitId.slice(0, 8)} with status ${verification.auth_status}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update authorization workflow')
    }
  }

  async function handleGenerateAiAssist(visitId: string) {
    setError(null)
    setMessage(null)
    try {
      const verification = await aiAssistMutation.mutateAsync(visitId)
      const source = verification.ai_assessment?.source === 'model' ? 'model-assisted' : 'heuristic assist'
      setMessage(`AI assist refreshed for visit ${visitId.slice(0, 8)} using ${source}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate insurance AI assist')
    }
  }

  if (!insuranceEnabled && featureFlagsQuery.isSuccess) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Insurance verification is not enabled for this tenant.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header className="v11-workspace-header">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
              Facility operations
            </p>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Insurance Verification</h1>
            <p className="mt-1 text-sm text-slate-600">
              Visit-anchored eligibility verification workspace for upcoming schedules. Live clearinghouse mode is currently bounded to Availity single-visit checks using per-request member inputs, while manual and provider-unconfigured modes remain available without PHI-rich logging.
            </p>
          </div>
          <button
            className="btn-primary text-sm md:w-auto"
            disabled={externalProviderMode || batchMutation.isPending || dashboardQuery.isLoading || visits.length === 0}
            onClick={() => void handleBatchVerify()}
          >
            {externalProviderMode ? 'Batch Verify Unavailable' : batchMutation.isPending ? 'Verifying...' : 'Batch Verify'}
          </button>
        </div>

        {dashboardQuery.data && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700">
              <Shield size={14} />
              Provider mode: {dashboardQuery.data.provider_mode}
            </span>
            <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
              Verified: {verifiedCount}
            </span>
            <span className="rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700">
              Pending: {pendingCount}
            </span>
            <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-700">
              Auth required: {authRequiredCount}
            </span>
            <span className="rounded-full bg-violet-50 px-3 py-1 font-semibold text-violet-700">
              Pending auth: {pendingAuthCount}
            </span>
          </div>
        )}

        {message && <p className="mt-3 text-sm text-emerald-700">{message}</p>}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {externalProviderMode && (
          <p className="mt-3 text-sm text-slate-600">
            Live provider mode requires payer/member details per visit. Batch verify stays disabled so the app does not invent hidden PHI persistence.
          </p>
        )}
      </header>

      <section className="v11-section p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Upcoming Visits</h2>
            <p className="mt-1 text-sm text-slate-600">
              Existing `site_visits` scheduled in the current verification window.
            </p>
          </div>
          <div className="flex flex-col gap-2 md:flex-row">
            {facilitiesQuery.data && facilitiesQuery.data.length > 1 && (
              <select
                value={selectedFacility}
                onChange={(event) => setSelectedFacility(event.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
              >
                <option value="">All Facilities</option>
                {facilitiesQuery.data.map((facility) => (
                  <option key={facility.facility_id} value={facility.facility_id}>
                    {facility.facility_name}
                  </option>
                ))}
              </select>
            )}
            <select
              value={viewMode}
              onChange={(event) => setViewMode(event.target.value as 'all' | 'auth_required' | 'pending_auth')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
            >
              <option value="all">All visits</option>
              <option value="auth_required">Auth required</option>
              <option value="pending_auth">Pending auth</option>
            </select>
          </div>
        </div>

        {dashboardQuery.isLoading && <p className="mt-4 text-sm text-slate-500">Loading insurance verification dashboard...</p>}
        {dashboardQuery.error && <p className="mt-4 text-sm text-red-600">{(dashboardQuery.error as Error).message}</p>}

        {!dashboardQuery.isLoading && !dashboardQuery.error && filteredVisits.length === 0 && (
          <div className="mt-4 rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
            <Clock3 size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-500">No visits match the current insurance workflow view</p>
          </div>
        )}

        {filteredVisits.length > 0 && (
          <div className="v11-section mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Facility</th>
                  <th className="px-4 py-3">Visit</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Auth Workflow</th>
                  <th className="px-4 py-3">Payer</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredVisits.map((visit) => {
                  const verification = visit.verification
                  const statusKey = verification?.verification_status ?? 'manual_review_required'
                  const eligibilityDraft = eligibilityDrafts[visit.visit_id] ?? {}
                  const authDraft = authDrafts[visit.visit_id] ?? {
                    authStatus: (verification?.auth_status ?? 'unknown') as AuthorizationStatus,
                    note: verification?.auth_workflow_note ?? '',
                    followUpDate: verification?.auth_follow_up_date ?? '',
                  }
                  const copay = readSummaryNumber(verification?.benefits_summary, 'copay_cents')
                  const deductible = readSummaryNumber(verification?.benefits_summary, 'deductible_remaining_cents')
                  const memberMask = readSummaryString(verification?.benefits_summary, 'member_id_last4')
                  const payerId = readSummaryString(verification?.benefits_summary, 'payer_id')
                  const planName = readSummaryString(verification?.benefits_summary, 'plan_name')
                  const verificationMessage = readSummaryString(verification?.verification_summary, 'message')
                  return (
                    <tr key={visit.visit_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{visit.facility_name}</div>
                        <div className="text-xs text-slate-500">{formatDate(visit.survey_date)} · {visit.visit_type}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{visit.visit_id.slice(0, 8)}...</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[statusKey] ?? STATUS_STYLES.manual_review_required}`}>
                          {statusKey === 'verified' ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                          {verification?.verification_status ?? 'not_started'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {verification ? (
                          <div className="space-y-2">
                            <select
                              aria-label={`Authorization status for ${visit.visit_id}`}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                              value={authDraft.authStatus}
                              onChange={(event) =>
                                setAuthDrafts((current) => ({
                                  ...current,
                                  [visit.visit_id]: { ...authDraft, authStatus: event.target.value as AuthorizationStatus },
                                }))
                              }
                            >
                              <option value="unknown">Unknown</option>
                              <option value="not_required">Not required</option>
                              <option value="required">Required</option>
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="denied">Denied</option>
                            </select>
                            <input
                              aria-label={`Authorization note for ${visit.visit_id}`}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                              placeholder="Auth workflow note"
                              value={authDraft.note}
                              onChange={(event) =>
                                setAuthDrafts((current) => ({
                                  ...current,
                                  [visit.visit_id]: { ...authDraft, note: event.target.value },
                                }))
                              }
                            />
                            <input
                              aria-label={`Authorization follow up for ${visit.visit_id}`}
                              type="date"
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                              value={authDraft.followUpDate}
                              onChange={(event) =>
                                setAuthDrafts((current) => ({
                                  ...current,
                                  [visit.visit_id]: { ...authDraft, followUpDate: event.target.value },
                                }))
                              }
                            />
                            {verification.auth_timeline.length > 0 && (
                              <p className="text-xs text-slate-500">
                                Last auth update: {verification.auth_timeline[verification.auth_timeline.length - 1]?.status ?? verification.auth_status}
                              </p>
                            )}
                            {(verificationMessage || planName || payerId || memberMask || copay !== null || deductible !== null) && (
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                                {verificationMessage && <p className="font-semibold text-slate-800">{verificationMessage}</p>}
                                <div className="mt-1 grid gap-1">
                                  {planName && <p>Plan: {planName}</p>}
                                  {payerId && <p>Payer ID: {payerId}</p>}
                                  {memberMask && <p>Member: {memberMask}</p>}
                                  {copay !== null && <p>Copay: {formatCurrencyFromCents(copay)}</p>}
                                  {deductible !== null && <p>Deductible remaining: {formatCurrencyFromCents(deductible)}</p>}
                                </div>
                              </div>
                            )}
                            {verification.ai_assessment && (
                              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900">
                                <div className="flex items-center gap-2 font-semibold">
                                  <Sparkles size={12} />
                                  AI assist: {verification.ai_assessment.denial_risk} denial risk
                                </div>
                                <p className="mt-1">
                                  Responsibility estimate: {formatCurrencyFromCents(verification.ai_assessment.patient_responsibility_estimate_cents)}.
                                  Confidence {Math.round(verification.ai_assessment.responsibility_confidence * 100)}%.
                                </p>
                                <p className="mt-1">{verification.ai_assessment.recommended_next_step}</p>
                                <p className="mt-1 text-[11px] text-violet-700">{verification.ai_assessment.limitation_summary}</p>
                                {verification.ai_assessment.auth_letter_draft && (
                                  <details className="mt-2">
                                    <summary className="cursor-pointer font-semibold">Review auth letter draft</summary>
                                    <pre className="mt-2 whitespace-pre-wrap rounded border border-violet-100 bg-white p-2 text-[11px] text-slate-700">
                                      {verification.ai_assessment.auth_letter_draft}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">Run verification first</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-2">
                          <input
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            placeholder="Optional payer name"
                            value={payerNames[visit.visit_id] ?? verification?.payer_name ?? ''}
                            onChange={(event) => setPayerNames((current) => ({ ...current, [visit.visit_id]: event.target.value }))}
                          />
                          {externalProviderMode && (
                            <div className="grid gap-2">
                              <input
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                placeholder="Payer ID"
                                value={eligibilityDraft.payerId ?? ''}
                                onChange={(event) =>
                                  setEligibilityDrafts((current) => ({
                                    ...current,
                                    [visit.visit_id]: { ...eligibilityDraft, payerId: event.target.value },
                                  }))
                                }
                              />
                              <input
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                placeholder="Member ID"
                                value={eligibilityDraft.memberId ?? ''}
                                onChange={(event) =>
                                  setEligibilityDrafts((current) => ({
                                    ...current,
                                    [visit.visit_id]: { ...eligibilityDraft, memberId: event.target.value },
                                  }))
                                }
                              />
                              <div className="grid gap-2 md:grid-cols-2">
                                <input
                                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                  placeholder="Patient first name"
                                  value={eligibilityDraft.patientFirstName ?? ''}
                                  onChange={(event) =>
                                    setEligibilityDrafts((current) => ({
                                      ...current,
                                      [visit.visit_id]: { ...eligibilityDraft, patientFirstName: event.target.value },
                                    }))
                                  }
                                />
                                <input
                                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                  placeholder="Patient last name"
                                  value={eligibilityDraft.patientLastName ?? ''}
                                  onChange={(event) =>
                                    setEligibilityDrafts((current) => ({
                                      ...current,
                                      [visit.visit_id]: { ...eligibilityDraft, patientLastName: event.target.value },
                                    }))
                                  }
                                />
                              </div>
                              <input
                                type="date"
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                aria-label={`Patient date of birth for ${visit.visit_id}`}
                                value={eligibilityDraft.patientDateOfBirth ?? ''}
                                onChange={(event) =>
                                  setEligibilityDrafts((current) => ({
                                    ...current,
                                    [visit.visit_id]: { ...eligibilityDraft, patientDateOfBirth: event.target.value },
                                  }))
                                }
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-2">
                          <button
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={verifyMutation.isPending}
                            onClick={() => void handleVerify(visit.visit_id)}
                          >
                            <RefreshCw size={14} />
                            {verification ? 'Re-verify' : 'Verify'}
                          </button>
                          <button
                            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                            disabled={authWorkflowMutation.isPending || !verification}
                            onClick={() => void handleAuthWorkflowSave(visit.visit_id)}
                          >
                            Save Auth
                          </button>
                          <button
                            className="inline-flex items-center gap-2 rounded-lg border border-violet-200 px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                            disabled={aiAssistMutation.isPending || !verification}
                            onClick={() => void handleGenerateAiAssist(visit.visit_id)}
                          >
                            <Sparkles size={14} />
                            Generate AI
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
